import { ForwardedRef, forwardRef } from 'react';
import { MenuProps } from 'antd/es/menu';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import opConfig from 'openpathConfig';

import { OpDropdown } from 'new-components/DLS/OpDropdown/OpDropdown';
import { useAwareDeploymentUrl } from 'utils/customHooks/useAwareDeploymentUrl';
import { AppSwitcherSvg } from 'components/svgs/AppSwitcherSvg';
import { useUnifiedLogin } from 'utils/customHooks/useUnifiedLogin';
import { getOpParams } from 'utils/getOpParams';
import { getDmpUrl } from 'utils/getDmpUrl';
import { useIsUnificationEnabled } from 'utils/customHooks/useIsUnificationEnabled';
import { useIsDmpEnabled } from 'utils/customHooks/useIsDmpEnabled';

import { AppSwitcherMenu } from './AppSwitcherMenu';
import { useNavigateToAccess } from './hooks/useNavigateToAccess';
import { useNavigateToMailroom } from './hooks/useNavigateToMailroom';
import { useNavigateToSos } from './hooks/useNavigateToSos';
import { useNavigateToVisitor } from './hooks/useNavigateToVisitor';
import { useNavigateToAdmin } from './hooks/useNavigateToAdmin';

import './AppSwitcher.scss';
/**
 * @component
 * @description Component used to app switch between Aware and Openpath. Utilizes the useUnifiedLogin
 * hook to handle the business logic necessary to properly authenticate and switch apps
 * @returns JSX.Element | null
 */
export const AppSwitcher = forwardRef(
  (_: unknown, ref: ForwardedRef<HTMLDivElement>) => {
    const { orgId } = getOpParams();

    const isAwareEnabled = useIsUnificationEnabled();
    const { isDmpEnabled } = useIsDmpEnabled();

    const awareDeploymentUrl = useAwareDeploymentUrl();
    const dmpUrl = getDmpUrl();

    // Aware login
    const {
      handleUnifiedLogin: handleAwareLogin,
      isLoggingIn: isAwareLoggingIn,
    } = useUnifiedLogin({
      url: awareDeploymentUrl,
      options: {
        org_id: orgId,
        region: opConfig.ENV,
      },
    });

    // dmp login
    const { handleUnifiedLogin: handleDmpLogin, isLoggingIn: isDmpLoggingIn } =
      useUnifiedLogin({
        url: dmpUrl,
        options: { org_id: orgId, region: opConfig.ENV },
      });

    // Navigate to access
    const { handleNavigateToAccess } = useNavigateToAccess(orgId);

    // Navigate to mailroom
    const { handleNavigateToMailroom } = useNavigateToMailroom(orgId);

    // Navigate to sos
    const { handleNavigateToSos } = useNavigateToSos(orgId);

    // Navigate to visitor
    const { handleNavigateToVisitor } = useNavigateToVisitor(orgId);

    // Navigate to admin
    const { handleNavigateToAdmin } = useNavigateToAdmin(orgId);

    const items: MenuProps['items'] = [
      {
        key: '1',
        className: 'app-switcher-overlay__menu-item',
        icon: (
          <AppSwitcherMenu
            {...(isAwareEnabled && { handleAwareLogin })}
            {...(isDmpEnabled && { handleDmpLogin })}
            handleNavigateToMailroom={handleNavigateToMailroom}
            handleNavigateToAccess={handleNavigateToAccess}
            handleNavigateToSos={handleNavigateToSos}
            handleNavigateToAdmin={handleNavigateToAdmin}
            handleNavigateToVisitor={handleNavigateToVisitor}
          />
        ),
      },
    ];

    const isLoading = isAwareLoggingIn || isDmpLoggingIn;

    return (
      <div data-testid="app-switcher" ref={ref}>
        <OpDropdown
          trigger={['click']}
          menu={{ items }}
          placement="bottomRight"
          arrow={{ pointAtCenter: true }}
          classNames={{ root: 'app-switcher-overlay' }}
        >
          <div className="app-switcher__icon">
            {isLoading ? <LoadingOutlined /> : <AppSwitcherSvg />}
          </div>
        </OpDropdown>
      </div>
    );
  },
);
